'use-strict'

import {
    drawDraggedCards,
    drawFoundation,
    drawStock,
    drawTableau,
    drawWaste,
    drawAnimatingFoundationOverlays,
} from './drawCards'
import {
    drawFoundationPlaceHolder,
    drawStockPlaceHolder,
    drawTableauPlaceHolder,
    drawWastePlaceHolder,
} from './drawPlaceholderOutlines'
import {
    drawUndoButton,
    drawMenuButton,
    drawNewGameButton,
    drawAutoCompleteButton,
    drawRecordsToggleButton,
} from './drawButtons'
import { drawMenu } from './drawMenu'
import { updateRenderDebugSnapshot } from './renderDebug'
import { drawTimer, drawCompletionTimer } from './drawTimer'
import { drawCompletionBackdrop, drawRecords } from './drawRecords'
import { appState } from './state'
import { CurrentAppState } from '../Models/AppState'
import { canAutoComplete } from './autocompleteGame'
import { isWinAnimationRunning, shouldShowBobRoss, startWinAnimation } from './winAnimation'

// ─── Cached references (looked up once, reused every frame) ─────────

let _canvas: HTMLCanvasElement | null = null
let _ctx: CanvasRenderingContext2D | null = null
let _bobRoss: HTMLElement | null = null

// Tracks the last canvas size so we only reallocate when dimensions change
let _lastCanvasW = 0
let _lastCanvasH = 0

// RAF deduplication — prevent multiple paint() per frame
let _paintScheduled = false
let _paintRafId: number | null = null

// Track last-rendered app state to skip redundant DOM class toggling
let _lastRenderedAppState: CurrentAppState | null = null

function ensureCachedElements(): boolean {
    if (!_canvas) {
        _canvas = document.getElementById('canvas') as HTMLCanvasElement
        if (!_canvas) return false
        _ctx = _canvas.getContext('2d')
    }
    if (!_bobRoss) {
        _bobRoss = document.querySelector('#BobRossDoingTheFlossLikeABoss') as HTMLElement
    }
    return _canvas !== null && _ctx !== null
}

/**
 * Schedule a paint on the next animation frame.
 * Multiple calls before the frame fires are coalesced into a single paint.
 */
export function schedulePaint(): void {
    if (_paintScheduled) return
    _paintScheduled = true
    _paintRafId = requestAnimationFrame(() => {
        _paintScheduled = false
        _paintRafId = null
        paint()
    })
}

/**
 * Cancel any pending schedulePaint RAF.
 * Called by the animation system to prevent stale paints from racing
 * with the animation tick (which handles its own painting).
 */
export function cancelScheduledPaint(): void {
    if (_paintRafId !== null) {
        cancelAnimationFrame(_paintRafId)
        _paintRafId = null
        _paintScheduled = false
    }
}

export function paint() {
    if (!ensureCachedElements()) return

    const canvas = _canvas!
    const ctx = _ctx!
    const dpr = window.devicePixelRatio || 1
    const targetW = appState.windowWidth * dpr
    const targetH = appState.windowHeight * dpr

    // Only reallocate the canvas buffer when dimensions actually change
    if (targetW !== _lastCanvasW || targetH !== _lastCanvasH) {
        canvas.width = targetW
        canvas.height = targetH
        canvas.style.width = `${appState.windowWidth}px`
        canvas.style.height = `${appState.windowHeight}px`
        _lastCanvasW = targetW
        _lastCanvasH = targetH
        // canvas.width reset clears transforms, so we must re-apply
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.scale(dpr, dpr)
    } else {
        // Clear without reallocating the buffer — much faster
        ctx.clearRect(0, 0, appState.windowWidth, appState.windowHeight)
    }

    // Solid background fill — flat, minimal, no gradient
    ctx.fillStyle = appState.currentTheme.backgroundColor
    ctx.fillRect(0, 0, appState.windowWidth, appState.windowHeight)

    switch (appState.currentAppState) {
        case CurrentAppState.Playing:
            // Only toggle DOM classes when app state actually changes
            if (_lastRenderedAppState !== CurrentAppState.Playing) {
                canvas.classList.remove('hidden')
                if (_bobRoss) {
                    // @ts-ignore
                    _bobRoss.style.display = 'none'
                }
                _lastRenderedAppState = CurrentAppState.Playing
            }
            drawFoundationPlaceHolder(ctx)
            drawTableauPlaceHolder(ctx)
            drawWastePlaceHolder(ctx)
            drawStockPlaceHolder(ctx)

            drawFoundation(ctx)
            drawTableau(ctx)
            drawWaste(ctx)
            drawStock(ctx)
            drawAnimatingFoundationOverlays(ctx)

            // Buttons drawn AFTER cards so they appear on top when long
            // tableau cascades extend into the button area
            drawNewGameButton(ctx)
            drawUndoButton(ctx)
            drawMenuButton(ctx)
            if (canAutoComplete()) {
                drawAutoCompleteButton(ctx)
            }

            drawTimer(ctx)

            drawDraggedCards(ctx)

            break
        case CurrentAppState.Completed:
            if (_lastRenderedAppState !== CurrentAppState.Completed) {
                canvas.classList.remove('hidden')
                // Bob Ross: only show on 1% easter egg
                if (_bobRoss) {
                    // @ts-ignore
                    _bobRoss.style.display = shouldShowBobRoss() ? 'block' : 'none'
                }
                _lastRenderedAppState = CurrentAppState.Completed

                // Start the win animation (card cascade + confetti)
                startWinAnimation()

                // Default to a clean win view; records are optional via toggle.
                appState.showWinRecords = false
            }

            // While the win animation is running, it handles its own rendering
            // via its own RAF loop. Only draw static elements here when it's done.
            if (!isWinAnimationRunning()) {
                drawNewGameButton(ctx)
                drawRecordsToggleButton(ctx)
                drawCompletionTimer(ctx)
                // Keep drawRecords hot-path alive for existing diagnostics/tests,
                // but only render visible content when the panel is enabled.
                drawRecords(ctx)

                if (appState.showWinRecords) {
                    // Overlay records only when explicitly requested.
                    drawCompletionBackdrop(ctx)
                    drawCompletionTimer(ctx)
                    drawRecords(ctx)
                    drawNewGameButton(ctx)
                    drawRecordsToggleButton(ctx)
                }
            }

            break
        case CurrentAppState.Menu:
            if (_lastRenderedAppState !== CurrentAppState.Menu) {
                canvas.classList.remove('hidden')
                if (_bobRoss) {
                    // @ts-ignore
                    _bobRoss.style.display = 'none'
                }
                _lastRenderedAppState = CurrentAppState.Menu
            }

            // Dim overlay so game content is visually behind the menu
            ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
            ctx.fillRect(0, 0, appState.windowWidth, appState.windowHeight)

            // Canvas-rendered menu: theme swatches, toggles, hit areas
            drawMenu(ctx)

            drawMenuButton(ctx)

            break
    }

    // Update per-frame render telemetry for logic-based e2e assertions.
    updateRenderDebugSnapshot()
}
